/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy,
	OnInit,
	OnDestroy,
	OnChanges,
	NgZone,
	Input,
} from '@angular/core';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';

import { EnterpriseDashboardService } from './enterprise-dashboard.service';
import { MemberAspectItemBase } from './member-aspect-item.base';
import { IMemberAspectItem } from './interfaces/IMemberAspectItem';

import { analyticsEventsService } from '../../plain-javascript/services/analytics/AnalyticsEventsService';
import { DASHBOARD_LOADING_PROMPT } from '@touchplan/strings';
import { timeout, take } from 'rxjs/operators';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';

@Component({
	selector: 'app-dashboard',
	templateUrl: './enterprise-dashboard.component.html',
	styleUrls: ['./enterprise-dashboard.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('EnterpriseDashboardComponent')],
})
export class EnterpriseDashboardComponent extends MemberAspectItemBase implements OnDestroy, OnChanges {
	@Input() public dashboardItem: IMemberAspectItem;

	@ViewChild('embedFrame', { static: false }) public dashboardFrame: ElementRef;

	// the dashboardUrl observable could change due to drilldown handling in the dashboard
	private dashboardUrlSubscription: Subscription;
	public dashboardUrl$ = new BehaviorSubject<string>(null);
	public dashboardFrameError$ = new BehaviorSubject<object>(null);
	public timeoutError$ = new BehaviorSubject<object>(null);
	public promptLoading = DASHBOARD_LOADING_PROMPT;

	private readonly timeoutThreshold = 30000; // milliseconds

	constructor(
		private window: Window,
		private ngZone: NgZone,
		private dashboardService: EnterpriseDashboardService,
		private logger: DefaultLoggerService
	) {
		super();

		this.setContentReady(false);
		this.addDashboardEventListener();
		this.dashboardService.setActiveDashboard(this);
	}

	public ngOnChanges(changes) {
		if (changes.dashboardItem) {
			const item = changes.dashboardItem.currentValue;
			// Don't set if it's the same dashboard ID
			if (!this.aspectItem || this.aspectItem.id !== item.id) {
				this.setAspectItem(item);
			}
		}
	}

	public ngOnDestroy() {
		if (this.dashboardUrlSubscription) {
			this.dashboardUrlSubscription.unsubscribe();
		}
		this.dashboardUrl$.complete();
	}

	// override
	public setAspectItem(aspectItem: IMemberAspectItem) {
		super.setAspectItem(aspectItem);

		// content not ready yet
		this.setContentReady(false);

		// add analytics event
		analyticsEventsService.openEmbeddedDashboard(
			aspectItem.id,
			aspectItem.name,
			aspectItem.memberId,
			aspectItem.organization
		);

		// get dashboard embed url
		this.getDashboardUrl();
	}

	// override
	public gotoRootContent() {
		if (this._inNestedContent$.getValue()) {
			this.setContentReady(false);

			// reload the frame
			this.getDashboardUrl();
		}

		super.gotoRootContent();
	}

	private getDashboardUrl() {
		if (this.dashboardUrlSubscription) {
			this.dashboardUrlSubscription.unsubscribe();
		}

		this.dashboardUrlSubscription = this.dashboardService
			.getBiEmbedUrl(this.aspectItem)
			.pipe(take(1), timeout(this.timeoutThreshold))
			.subscribe(
				(url) => {
					if (url) {
						this.dashboardUrl$.next(url);
					}
				},
				(error) => {
					this.setContentReady(true);
					this.timeoutError$.next(error);
				}
			);
	}

	public onLoad($e, frame) {
		try {
			$e.target.contentDocument;
		} catch (err) {
			this.logger.error(err);
			this.setContentReady(true);
			this.dashboardFrameError$.next($e);
		}
	}

	public onError($e) {
		this.setContentReady(true);
		this.dashboardFrameError$.next($e);
		this.logger.log($e);
	}

	// this for handling events from Looker for adding customization properly
	private addDashboardEventListener() {
		this.window.onmessage = (event) => {
			if (
				this.dashboardFrame &&
				event['origin'] === 'https://analytics.touchplan.io' &&
				event['source'] === this.dashboardFrame.nativeElement['contentWindow']
			) {
				// need to run the event handler code in NgZone to trigger the dirty check and UI update
				this.ngZone.run(
					(eventArray) => {
						try {
							const data = JSON.parse(eventArray['data']);
							// this.logger.log(data['type']);
							if (data['type'] === 'dashboard:loaded') {
								// move change to true to the very end of dashboard loading now: dashboard:tun:complete
								// this.setContentReady(true);
							} else if (data['type'] === 'dashboard:run:complete') {
								this.setContentReady(true);
							}
						} catch (e) {
							this.logger.error(e);
							// JSON parse fails with a specific event
						}
					},
					true,
					[event]
				);
			}
		};
	}
}
